import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageTitle, Panel, Btn, Field, Input, Select, Textarea, Modal, DataTable, Toolbar, exportCsv, Plus, toast } from "@/pages/app/kit";
import { StatusBadge, RiskBadge, DemoBadge } from "@/components/shared";
import FileUpload from "@/components/FileUpload";

const EMPTY = {
  product: "", variety: "", category: "fruit", expected_quantity: 0, quantity_unit: "kg",
  harvest_start: "", harvest_end: "", min_viable_price: 0, preferred_price: 0, highest_offer: 0,
  location: "", region: "lubelskie", refrigeration_required: false, transport_required: true,
  transport_secured: false, cold_storage_secured: false, notes: "", processing_acceptable: true,
  fresh_grade_estimate: 0, processing_grade_estimate: 0, status: "Forecast",
};

export default function CropsPage() {
  const { t } = useT();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [filesCrop, setFilesCrop] = useState(null);
  const isManager = ["coordinator", "admin", "super_admin"].includes(user.role);

  const load = () => api.get("/crops").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      ["expected_quantity", "min_viable_price", "preferred_price", "highest_offer", "fresh_grade_estimate", "processing_grade_estimate"].forEach((k) => payload[k] = parseFloat(payload[k]) || 0);
      await api.post("/crops", payload);
      toast.success(t("cta.create"));
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const filtered = rows.filter((r) => r.product.toLowerCase().includes(search.toLowerCase()));

  const columns = [
    { key: "product", label: t("crop.product"), render: (r) => (
      <div><div className="font-semibold text-charcoal">{r.product}{r.is_demo && <DemoBadge className="ml-2" />}</div><div className="text-xs text-mutedtext">{r.variety}</div></div>
    )},
    { key: "region", label: t("crop.region"), render: (r) => <span className="capitalize">{r.region}</span> },
    { key: "expected_quantity", label: t("crop.quantity"), render: (r) => `${r.expected_quantity?.toLocaleString()} ${r.quantity_unit}` },
    { key: "matched", label: t("crop.matched"), render: (r) => `${((r.matched_fresh||0)+(r.matched_processing||0)).toLocaleString()} ${r.quantity_unit}` },
    { key: "remaining", label: t("crop.remaining"), render: (r) => <span className={r.risk.remaining>0?"font-semibold text-crittext":""}>{r.risk.remaining.toLocaleString()}</span> },
    { key: "status", label: t("crop.status"), render: (r) => <StatusBadge status={r.status} /> },
    { key: "risk", label: "Risk", render: (r) => r.risk.at_risk ? <RiskBadge priority={r.risk.priority} /> : <span className="text-xs text-oktext">OK</span> },
    { key: "act", label: "", render: (r) => (
      <div className="flex gap-2">
        {isManager && <Link to={`/app/matching?crop=${r.id}`} className="text-xs font-semibold text-forest hover:underline">Match</Link>}
        <button className="text-xs font-semibold text-soil hover:underline" onClick={() => setFilesCrop(r)} data-testid={`files-${r.id}`}>Files</button>
        <Link to={`/trace/${r.trace_id}`} className="text-xs font-semibold text-gold hover:underline">Trace</Link>
      </div>
    )},
  ];

  return (
    <div>
      <PageTitle title={isManager ? t("dashboard.myCrops") : t("crop.title")}
        subtitle={isManager ? "" : t("crop.title")}
        action={(user.role === "farmer" || isManager) && <Btn icon={Plus} onClick={() => setOpen(true)} data-testid="add-crop-btn">{t("cta.registerCrop")}</Btn>} />
      <Toolbar search={search} setSearch={setSearch} onExport={() => exportCsv("crops.csv", filtered)} />
      <DataTable columns={columns} rows={filtered} empty={t("empty")} testid="crops-table" />

      <Modal open={open} onClose={() => setOpen(false)} title={t("crop.title")} wide>
        <form onSubmit={submit} className="space-y-4" data-testid="crop-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("crop.product")} required><Input data-testid="crop-product" value={form.product} onChange={set("product")} required /></Field>
            <Field label={t("crop.variety")}><Input value={form.variety} onChange={set("variety")} /></Field>
            <Field label={t("crop.quantity")} required><Input data-testid="crop-qty" type="number" value={form.expected_quantity} onChange={set("expected_quantity")} /></Field>
            <Field label={t("crop.unit")}><Select value={form.quantity_unit} onChange={set("quantity_unit")}><option>kg</option><option>t</option></Select></Field>
            <Field label={t("crop.harvestStart")}><Input data-testid="crop-harvest-start" type="date" value={form.harvest_start?.slice(0,10)} onChange={set("harvest_start")} /></Field>
            <Field label={t("crop.harvestEnd")}><Input type="date" value={form.harvest_end?.slice(0,10)} onChange={set("harvest_end")} /></Field>
            <Field label={t("crop.minPrice")} required><Input data-testid="crop-min-price" type="number" step="0.01" value={form.min_viable_price} onChange={set("min_viable_price")} /></Field>
            <Field label={t("crop.offer")}><Input type="number" step="0.01" value={form.highest_offer} onChange={set("highest_offer")} /></Field>
            <Field label={t("crop.freshEst")}><Input type="number" value={form.fresh_grade_estimate} onChange={set("fresh_grade_estimate")} /></Field>
            <Field label={t("crop.procEst")}><Input type="number" value={form.processing_grade_estimate} onChange={set("processing_grade_estimate")} /></Field>
            <Field label={t("crop.location")}><Input value={form.location} onChange={set("location")} placeholder="Lublin" /></Field>
            <Field label={t("crop.region")}><Input value={form.region} onChange={set("region")} /></Field>
          </div>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.refrigeration_required} onChange={set("refrigeration_required")} /> {t("crop.refrigeration")}</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.transport_required} onChange={set("transport_required")} /> {t("crop.transport")}</label>
          </div>
          <Field label={t("crop.notes")}><Textarea rows={2} value={form.notes} onChange={set("notes")} /></Field>
          <div className="flex gap-3"><Btn type="submit" disabled={busy} data-testid="crop-submit">{busy ? "…" : t("cta.create")}</Btn><Btn type="button" variant="ghost" onClick={() => setOpen(false)}>{t("cta.cancel")}</Btn></div>
        </form>
      </Modal>
      <Modal open={!!filesCrop} onClose={() => setFilesCrop(null)} title={`${filesCrop?.product || ""} — Files`}>
        {filesCrop && <FileUpload entityId={filesCrop.id} category="crop_photo" label="Crop photos & documents" />}
      </Modal>
    </div>
  );
}
